import { Box, Button, Flex, SectionButton } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { ColoredSquare, GuestCard, GuestCardsContainer, PersonChip } from '../Accommodation.style'
import { CheckIcon } from '../icons/CheckIcon'
import { ExclamationMarkIcon } from '../icons/ExclamationMarkIcon'
import { ClockIcon } from '../icons/ClockIcon'
import { useEffect, useState } from 'react'
import { LocationIcon } from '../icons/LocationIcon'
import { NextIcon } from '../icons/NextIcon'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { checkOut, getAccommodationGuests } from 'features/accommodation/accommodationSlice'
import { IGetAllAccommodationGuestsSingleResponse } from 'types/api/accommodationApi.interface'
import { formatDateForDisplay, getDateDifference } from 'utils/helpers'
import { setConfirmModal } from 'features/global/globalSlice'
import { ModalTypes } from 'types/global.interface'
import { ExtendStayModal } from './ExtendStayModal'
import { NoData } from 'components/NoData'

export const Guests = () => {
  const [active, setActive] = useState('All Guests')
  const [params, setParams] = useState<any>({ page: 1, limit: 99, personType: 2, filter: 1 })
  const dispatch = useDispatch<AppDispatch>()
  const { guests } = useSelector((state: RootState) => state.accomodation)
  const [modal, setModal] = useState({ open: false, id: null })

  const handleActiveChange = (status: string) => {
    setActive(status)
    switch (status) {
      case 'All Guests':
        setParams({ ...params, filter: 1 })
        break
      case 'Overstayed':
        setParams({ ...params, filter: 2 })
        break
      case 'Expiring Soon':
        setParams({ ...params, filter: 3 })
        break
      case 'Active':
        setParams({ ...params, filter: 4 })
        break
      default:
        setParams({ ...params, filter: 1 })
    }
  }


  useEffect(() => {
    dispatch(getAccommodationGuests(params))
  }, [dispatch, params])

  const StatusBox = ({ title, count }) => {
    const getSquareColor = () => {
      switch (title) {
        case 'Total':
          return colors.paleOrange
        case 'Overstayed':
          return colors.paleRed
        case 'Expiring Soon':
          return colors.paleYellow
        default:
          return colors.paleGreen
      }
    }

    const getIcon = () => {
      switch (title) {
        case 'Total':
          return <CheckIcon />
        case 'Overstayed':
          return <ExclamationMarkIcon />
        case 'Expiring Soon':
          return <ClockIcon color={colors.yellow} />
        default:
          return <CheckIcon />
      }
    }

    return (
      <Box width='100%'>
        <Flex column gap='1rem'>
          <TitleText weight='bold'>{title || 'Total'}</TitleText>
          <Flex width='100%' between>
            <ColoredSquare width='36px' color={getSquareColor()}>
              {getIcon()}
            </ColoredSquare>
            <TitleText style={{ fontSize: 28 }}>{count || 0}</TitleText>
          </Flex>
        </Flex>
      </Box>
    )
  }

  const handleCheckOut = (id: number) => {
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(checkOut(id))
          await dispatch(getAccommodationGuests(params))
        },
        setParams: {
          type: ModalTypes.BeforeSabe,
          f: setParams,
        },
        word: 'check out this person',
      }),
    )
  }

  const Guest = ({ data }: { data: IGetAllAccommodationGuestsSingleResponse }) => {
    const guest = data.accommodationGuest

    return (
      <GuestCard>
        <Flex column width='100%' gap='0.5rem'>
          <Flex gap='0.5rem'>
            <TitleText style={{ fontSize: 18 }}>
              {guest?.firstName} {guest?.lastName}
            </TitleText>
            {/* {!!data.actualCheckOutTimestamp && <ExclamationMarkIcon />} */}
          </Flex>
          <Flex gap='1rem' center>
            <PersonChip type='Guest'>Guest</PersonChip>
            <OtherText grey>ID: {guest?.identificationNumber || '/'}</OtherText>
          </Flex>
        </Flex>
        <Flex gap='1rem' width='100%'>
          <Box width='100%'>
            <Flex column gap='0.5rem'>
              <OtherText grey>Check-In</OtherText>
              <BodyText>{formatDateForDisplay(data.checkInTimestamp, true)}</BodyText>
            </Flex>
          </Box>
          <Box width='100%'>
            <Flex column gap='0.5rem'>
              <OtherText grey>Check-Out</OtherText>
              <BodyText>{formatDateForDisplay(data.expectedCheckOutTimestamp, true)}</BodyText>
            </Flex>
          </Box>
        </Flex>
        <Flex column gap='1.5rem' width='100%'>
          <Flex column gap='0.5rem'>
            <OtherText grey>Bed Location</OtherText>
            <Flex gap='0.5rem ' center wrap>
              <LocationIcon />
              <BodyText>{data.campName || '/'}</BodyText>
              <NextIcon />
              <BodyText>{data.blockName || '/'}</BodyText>
              <NextIcon />
              <BodyText>{data.roomName || '/'}</BodyText>
              <NextIcon />
              <BodyText color='#ff9900'>Bed {data.bedName || '/'}</BodyText>
            </Flex>
          </Flex>
          <Flex column gap='0.5rem'>
            <OtherText grey>Gender / Requested By</OtherText>
            <BodyText>
              {(guest?.gender || '/').toUpperCase()} /{' '}
              {guest?.requestedBy
                ? `${guest.requestedBy.firstName} ${guest.requestedBy.lastName}`
                : '/'}
            </BodyText>
          </Flex>
          <Flex mt='16' between width='100%'>
            <BodyText grey>Total Stay Duration</BodyText>
            <BodyText>{getDateDifference(data.checkInTimestamp, data.expectedCheckOutTimestamp)}</BodyText>
          </Flex>
          <Flex width='100%' gap='1rem'>
            <Button orange style={{ flex: 1 }} onClick={() => setModal({ open: true, id: data.id })} >Extend Stay</Button>
            <Button green style={{ flex: 1 }} onClick={() => handleCheckOut(data.id)}>Check Out</Button>
          </Flex>
        </Flex>
      </GuestCard>
    )
  }

  return (
    <>
      <Flex mt='32' mb='32' gap='1rem'>
        <StatusBox title='Total' count={guests?.total || 0} />
        <StatusBox title='Overstayed' count={guests?.overstayed || 0} />
        <StatusBox title='Expiring Soon' count={guests?.expiringSoon || 0} />
        <StatusBox title='Active' count={guests?.active || 0} />
      </Flex>
      <Box width='100%'>
        <Flex gap='1rem'>
          <SectionButton active={active === 'All Guests'} onClick={() => handleActiveChange('All Guests')}>
            All Guests
          </SectionButton>
          <SectionButton active={active === 'Overstayed'} onClick={() => handleActiveChange('Overstayed')}>
            Overstayed
          </SectionButton>
          <SectionButton
            active={active === 'Expiring Soon'}
            onClick={() => handleActiveChange('Expiring Soon')}
          >
            Expiring Soon
          </SectionButton>
          <SectionButton active={active === 'Active'} onClick={() => handleActiveChange('Active')}>
            Active
          </SectionButton>
        </Flex>
      </Box>
      <GuestCardsContainer >
        {guests?.data.length && guests?.data.map((guest, i) => guest && <Guest key={i} data={guest} />)}
      </GuestCardsContainer>
      {!guests?.data.length && <NoData text='No guests found for selected criteria' />}
      {modal.open && <ExtendStayModal closeModal={() => setModal({ open: false, id: null })} id={modal.id} />}
    </>
  )
}

const colors = {
  red: '#E74C3C',
  paleRed: '#E74C3C4D',
  orange: '#ff9900',
  paleOrange: '#FF99004D',
  yellow: '#F4D03F',
  paleYellow: '#F4D03F4D',
  green: '#24D07B',
  paleGreen: '#24D07B4D',
  blue: '#00A6FF',
}
